/**
 * A single step in the agent's chain of thought, split for the accordion.
 *
 * `label` is the row's visible one-liner; `body` is what expanding reveals, and
 * is null when the step is a single line with nothing more to show — those rows
 * render without a chevron rather than opening onto an empty panel.
 */
export type ReasoningStep = {
	label: string;
	body: string | null;
};

/**
 * Split one chain-of-thought text into its row label and its body.
 *
 * The first non-empty line is the label. Reasoning parts read as a lead sentence
 * followed by detail, so that line is a usable summary; using the whole text as
 * the label would put a paragraph in a row that is one line tall.
 */
export function splitReasoningStep(text: string): ReasoningStep | null {
	const lines = text.split("\n");

	let labelIndex = -1;
	for (let i = 0; i < lines.length; i++) {
		if (lines[i].trim()) {
			labelIndex = i;
			break;
		}
	}

	// Nothing but whitespace. The caller drops it rather than drawing an empty row.
	if (labelIndex === -1) {
		return null;
	}

	const body = lines
		.slice(labelIndex + 1)
		.join("\n")
		.trim();

	return {
		label: lines[labelIndex].trim(),
		body: body.length > 0 ? body : null,
	};
}

/**
 * The steps to render, from whatever the caller passed as children.
 *
 * Accepts a single string so existing callers keep working; it becomes one step.
 * Adjacent duplicates are dropped — the streamed parts can repeat a line as they
 * are revised, and the same sentence twice in a row reads as a bug.
 */
export function toReasoningSteps(children: string | string[]): ReasoningStep[] {
	const texts = Array.isArray(children) ? children : [children];

	return texts
		.filter((text, i, all) => i === 0 || text !== all[i - 1])
		.map(splitReasoningStep)
		.filter((step): step is ReasoningStep => step !== null);
}
